import axios, { AxiosResponse } from "axios";
import { put, takeEvery, takeLatest, select, PutEffect, SelectEffect } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";
import {
    analysisZoneAndSubZonesCreatedorEditedInterface,
    copySelectedZonesAction,
    createAnalysisZoneAndSubZonesAction,
    deleteSelectedZonesAction,
    deleteZoneComponentsAction,
    editAnalysisZoneAndSubZonesAction,
    exportMultipleZonesInterface,
    EXPORT_MULTIPLE_ZONES,
    getAllProposedAnalysisZonesAction,
    GetDataForAnalyzerFiltersAction,
    getLinksInPolygonAction,
    getZoneComponentsAction,
    polygonFeatures,
    linksInPolygonsReceivedAction,
    allProposedAnalysisZonesReceivedAction,
    analysisZoneAndSubZonesEditedAction,
    AnalyzerFilters,
    DataForAnalyzerFiltersReceivedAction
} from "../actions/Analyzer/types";
import {
    GeneratorYieldType,
    GetResponseInterface
} from "interfaces/ActionReducer/GetMethod/GetResponse";
import {
    exportFileExtentionForAnalyzer,
    exportFileType
} from "components/curb/ManagerAnalyzer/Manager/ListviewTable/helpers";
import { parseDateWithDots } from "Utilities/dateTimeUtilities";
import { RootState } from "reducers";
import { Features, Properties } from "components/curb/ManagerAnalyzer/Analyzer/types";
import { cloneDeep } from "lodash";
import i18next from "i18next";
import { store } from "../store";
import { showError, showPopUp } from "../components/commonUtilities";
import { storeBatchLinks } from "../actions/regulation/BatchSelection";
import {
    DELETE_SELECTED_ZONES,
    COPY_SELECTED_ZONES,
    GET_LINKS_IN_POLYGON,
    linksInPolygonsReceived,
    GET_ALL_PROPOSED_ANALYSIS_ZONES,
    allProposedAnalysisZonesReceived,
    getAllProposedAnalysisZones,
    CREATE_ANALYSIS_ZONE_AND_SUB_ZONES,
    analysisZoneAndSubZonesCreated,
    GET_ZONE_COMPONENTS,
    zoneComponentsReceived,
    analysisZoneAndSubZonesEdited,
    EDIT_ANALYSIS_ZONE_AND_SUB_ZONES,
    DELETE_ZONE_COMPONENTS,
    GET_DATA_FOR_ANALYZER_FILTERS,
    dataForAnalyzerFiltersReceived
} from "../actions/Analyzer";
import {
    getLinksInBatchAction,
    GET_LINKS_IN_BATCH,
    storeBatchLinksAction
} from "../actions/regulation/BatchSelection/types";
import { GetRequestSingleItemInterface } from "../interfaces/ActionReducer/GetMethod/GetRequestSingleItem";
import { CurrentCityReceived } from "../actions/City/types";
import { CurbLinkRecord } from "interfaces/CurbLink";

const polygonFeaturesSelector = (state: RootState) => state.Analyzer.polygonFeatures;
const segmentFeaturesSelector = (state: RootState) => state.Analyzer.segmentFeatures;
const listViewFilterParams = (state: RootState) => state.Analyzer.listViewFilterParams;
const dictionaryCurbLinks = (state: RootState) => state.globals.dictionaryCurbLinks;

function* getLinksInPolygon(
    action: getLinksInPolygonAction | getLinksInBatchAction
): Generator<
    | Promise<storeBatchLinksAction>
    | Promise<linksInPolygonsReceivedAction>
    | SelectEffect
    | GeneratorYieldType<storeBatchLinksAction["payload"]>
    | GeneratorYieldType<linksInPolygonsReceivedAction["payload"]>,
    void,
    any
> {
    try {
        const json = yield axios
            .post("/api/links/get_assets_and_links_in_polygon/", action.payload)
            .then((res) => res.data);

        if (action.type === "GET_LINKS_IN_BATCH") {
            const prevData = cloneDeep(store.getState().batchSelection.batchLinks);
            const dict = yield select(dictionaryCurbLinks);
            let filteredLinks = json;
            // If geoserver is enabled, then there is only 1 default record with id -1
            filteredLinks.links = json.links.filter(
                (i: CurbLinkRecord) => dict[i.id] || dict.hasOwnProperty(-1)
            );
            prevData[action.payload.uuid] = filteredLinks;
            yield put(storeBatchLinks({ batchLinks: prevData }));
        } else {
            json.uuid = uuidv4();
            yield put(linksInPolygonsReceived({ response: json }));
        }
    } catch (err) {
        showError(err);
        yield put(linksInPolygonsReceived({ response: err as GetResponseInterface }));
    }
}

export function* getLinksInPolygonSaga() {
    yield takeLatest([GET_LINKS_IN_POLYGON, GET_LINKS_IN_BATCH], getLinksInPolygon);
}

function* getAllProposedAnalysisZone(
    action: getAllProposedAnalysisZonesAction
): Generator<
    | Promise<allProposedAnalysisZonesReceivedAction>
    | GeneratorYieldType<allProposedAnalysisZonesReceivedAction["payload"]>,
    void,
    allProposedAnalysisZonesReceivedAction["payload"]["data"]
> {
    let url = "/api/analysis-zone/";

    if (action.payload.params) {
        url += "?";
        Object.keys(action.payload.params)?.forEach((prop: string) => {
            if (action.payload.params && Array.isArray(action.payload.params[prop])) {
                const allFilters = action.payload.params[prop].toString();

                url += `&${prop}=${allFilters}`;

                /*
                 * Object.values(action.params[prop]).forEach((key) => {
                 *     // Add multiple filters, if present
                 *     url += `&${prop}=${key}`;
                 * });
                 */
            } else {
                url += `&${prop}=${action.payload.params && action.payload.params[prop]}`;
            }
        });
    }

    try {
        const json = yield axios.get(url).then((res) => res.data);

        yield put(allProposedAnalysisZonesReceived({ data: json }));
    } catch (error) {
        showError(error);
    }
}

export function* getAllProposedAnalysisZonesSaga() {
    yield takeLatest(GET_ALL_PROPOSED_ANALYSIS_ZONES, getAllProposedAnalysisZone);
}

function* deleteSelectedZones(
    action: deleteSelectedZonesAction
): Generator<
    | Promise<void | AxiosResponse<GetRequestSingleItemInterface | undefined>[]>
    | SelectEffect
    | GeneratorYieldType<getAllProposedAnalysisZonesAction["payload"]>,
    void,
    GetRequestSingleItemInterface | undefined
> {
    try {
        yield Promise.all(
            action.payload.ids.map((id) => axios.delete(`api/analysis-zone/${id}/`))
        ).catch((e) => showError(e));
        const params = yield select(listViewFilterParams);

        yield put(getAllProposedAnalysisZones({ params }));
    } catch (error) {
        showError(error);
    }
}

export function* deleteSelectedZonesSaga() {
    yield takeLatest(DELETE_SELECTED_ZONES, deleteSelectedZones);
}

function* copySelectedZones(
    action: copySelectedZonesAction
): Generator<
    | SelectEffect
    | Promise<void | AxiosResponse<GetRequestSingleItemInterface | undefined>[]>
    | GeneratorYieldType<getAllProposedAnalysisZonesAction["payload"]>,
    void,
    GetRequestSingleItemInterface | undefined
> {
    try {
        yield Promise.all(
            action.payload.analysisZoneIds.map((analysisZoneId) =>
                axios.get(`api/analysis-zone/${analysisZoneId}/copy_analysis_zone/`)
            )
        ).catch((e) => showError(e));
        const params = yield select(listViewFilterParams);

        yield put(getAllProposedAnalysisZones({ params }));
    } catch (error) {
        showError(error);
    }
}

export function* copySelectedZonesSaga() {
    yield takeLatest(COPY_SELECTED_ZONES, copySelectedZones);
}

function* deleteZoneComponents(action: deleteZoneComponentsAction) {
    try {
        yield Promise.all(
            action.payload.data.map((id) => axios.delete(`api/zone-component/${id}/`))
        ).catch((e) => showError(e));
    } catch (error) {
        showError(error);
    }
}

export function* deleteZoneComponentsSaga() {
    yield takeLatest(DELETE_ZONE_COMPONENTS, deleteZoneComponents);
}

function* createAnalysisZoneAndSubZones(action: createAnalysisZoneAndSubZonesAction): Generator<
    | Promise<
          {
              zone_id: number;
              type: string;
              name: string;
              spaces: any;
              segments: any[];
              segment_geometry: any;
          }[]
      >
    | GeneratorYieldType<{ response: analysisZoneAndSubZonesCreatedorEditedInterface }>,
    void,
    analysisZoneAndSubZonesCreatedorEditedInterface
> {
    try {
        const { formData } = action.payload;

        if (!formData.currentUser) return;
        const zonePayload = {
            name: formData.name,
            description: formData.description,
            total_spaces: 0
        };

        const json = yield axios.post("/api/analysis-zone/", zonePayload).then((res) => res.data);

        yield Promise.all(
            formData.subZones.map((subZone: polygonFeatures, index: polygonFeatures) => {
                const subZonePayLoad = {
                    zone_id: json.id,
                    type: subZone.shapeType,
                    name: `${Number(index) + 1} ${subZone.shapeType}`,
                    spaces: subZone.spacesCount,
                    segments: subZone.links,
                    geometry: subZone.features[0].geometry
                };

                return axios
                    .post("/api/zone-component/", subZonePayLoad)
                    .then((res) => res.data)
                    .catch((e) => showError(e));
            })
        );
        let count = 0;

        yield Promise.all(
            Object.keys(formData.segments).map((key) => {
                const subZonePayLoad = {
                    zone_id: json.id,
                    type: "Segments",
                    name: `${count++} Segment`,
                    spaces: formData.segments[key].bays_count,
                    segments: [formData.segments[key].id],
                    segment_geometry: formData.segments[key].geometry
                };

                return axios
                    .post("/api/zone-component/", subZonePayLoad)
                    .then((res) => res.data)
                    .catch((e) => showError(e));
            })
        );

        yield put(analysisZoneAndSubZonesCreated({ response: json }));
    } catch (err) {
        const { response } = err as {
            response: {
                data: {
                    name: string;
                };
                status: number;
            };
        };

        if (response.status === 400 && response.data.name) {
            showPopUp(
                response.data.name,
                i18next.t("Error"),
                "",
                "",
                () => {
                    /* Do nothing*/
                },
                false
            );
        } else {
            showError(err);
        }
        yield put(
            analysisZoneAndSubZonesCreated({
                response: err as analysisZoneAndSubZonesCreatedorEditedInterface
            })
        );
    }
}

export function* createAnalysisZoneAndSubZonesSaga() {
    yield takeLatest(CREATE_ANALYSIS_ZONE_AND_SUB_ZONES, createAnalysisZoneAndSubZones);
}

function* editAnalysisZoneAndSubZones(action: editAnalysisZoneAndSubZonesAction): Generator<
    | SelectEffect
    | Promise<
          {
              zone_id: number;
              type: any;
              name: string;
              spaces: any;
              segments: any;
              geometry: any;
          }[]
      >
    | GeneratorYieldType<analysisZoneAndSubZonesEditedAction["payload"]>,
    void,
    analysisZoneAndSubZonesCreatedorEditedInterface
> {
    try {
        const { data } = action.payload;

        if (!data.currentUser) return;
        const zonePayload = {
            name: data.name,
            description: data.description,
            total_spaces: 0
        };

        const json = yield axios
            .patch(`/api/analysis-zone/${data.id}/`, zonePayload)
            .then((res) => res.data);

        const polygonIndexes: number[] = [];
        const circleIndexes: number[] = [];
        const polygonFeatures = yield select(polygonFeaturesSelector);

        Object.values(polygonFeatures).forEach((value) => {
            (value.features as Features[]).forEach((feature) => {
                if ((feature.properties as Properties).name) {
                    if ((feature.properties as Properties).type === "Polygon") {
                        polygonIndexes.push(
                            Number(
                                ((feature.properties as Properties).name as string).split(" ")[0]
                            )
                        );
                    } else if (feature.properties) {
                        circleIndexes.push(
                            Number(
                                ((feature.properties as Properties).name as string).split(" ")[0]
                            )
                        );
                    }
                }
            });
        });

        polygonIndexes.sort();
        circleIndexes.sort();

        const finalPolygonIndex = polygonIndexes.length
            ? polygonIndexes[polygonIndexes.length - 1] + 1
            : 0;

        const finalCircleIndex = circleIndexes.length
            ? circleIndexes[circleIndexes.length - 1] + 1
            : 0;
        const finalIndex = (shape: string) =>
            shape.toLowerCase() === "polygon" ? finalPolygonIndex : finalCircleIndex;

        yield Promise.all(
            data.subZones.map((subZone: polygonFeatures, index: number) => {
                const subZonePayLoad = {
                    zone_id: json.id,
                    type: subZone.shapeType,
                    name: `${index + finalIndex(subZone.shapeType)} ${subZone.shapeType}`,
                    spaces: subZone.spacesCount,
                    segments: subZone.links,
                    geometry: subZone.features[0].geometry
                };

                return axios
                    .post("/api/zone-component/", subZonePayLoad)
                    .then((res) => res.data)
                    .catch((e) => showError(e));
            })
        );

        const segmentIndexes: number[] = [];
        const segmentFeatures = yield select(segmentFeaturesSelector);

        Object.values(segmentFeatures).forEach((value) => {
            value.features.forEach((feature: any) => {
                feature.properties.name &&
                    segmentIndexes.push(Number(feature.properties.name.split(" ")[0]));
            });
        });

        segmentIndexes.sort();

        const finalSegmentIndex = segmentIndexes.length
            ? segmentIndexes[segmentIndexes.length - 1] + 1
            : 0;

        yield Promise.all(
            Object.keys(data.segments).map((key, index) => {
                const subZonePayLoad = {
                    zone_id: json.id,
                    type: "Segments",
                    name: `${index + finalSegmentIndex} Segment`,
                    spaces: data.segments[key].bays_count,
                    segments: [data.segments[key].id],
                    segment_geometry: data.segments[key].geometry
                };

                return axios
                    .post("/api/zone-component/", subZonePayLoad)
                    .then((res) => res.data)
                    .catch((e) => showError(e));
            })
        );

        yield put(analysisZoneAndSubZonesEdited({ data: json }));
    } catch (err) {
        showError(err);
        yield put(
            analysisZoneAndSubZonesEdited({
                data: err as analysisZoneAndSubZonesCreatedorEditedInterface
            })
        );
    }
}

export function* editAnalysisZoneAndSubZonesSaga() {
    yield takeLatest(EDIT_ANALYSIS_ZONE_AND_SUB_ZONES, editAnalysisZoneAndSubZones);
}

function* getZoneComponents(action: getZoneComponentsAction): Generator<any, void, any> {
    try {
        const json = yield axios
            .get("api/zone-component/", { params: action.payload.params })
            .then((res) => res.data);

        if (action.payload.params.zone__id__in) {
            json.multipleZoneIds = action.payload.params.zone__id__in;
        }
        if (action.payload.params.zone__id) {
            json.zoneId = action.payload.params.zone__id;
        }
        yield put(zoneComponentsReceived({ response: json }));
    } catch (err) {
        showError(err);
        yield put(zoneComponentsReceived({ response: err as GetResponseInterface }));
    }
}

export function* getZoneComponentsSaga() {
    yield takeEvery(GET_ZONE_COMPONENTS, getZoneComponents);
}

function* exportMultipleZones(
    action: exportMultipleZonesInterface
): Generator<Promise<CurrentCityReceived | void>, void, CurrentCityReceived["payload"]["data"]> {
    try {
        const blobFileTypes = new Set([exportFileType.pdf, exportFileType.excel]);
        const city = yield axios
            .get("/api/cityinfo/currentcity/")
            .then((response) => response.data);
        const fileName = `Proposed_Changes-${city.name}-${parseDateWithDots(new Date())}`;
        const { fileType, zoneIds } = action.payload;
        yield axios
            .post(
                `/api/analysis-zone/export_analysis_zone_${fileType}/`,
                {
                    analysis_zones: zoneIds
                },
                {
                    responseType: blobFileTypes.has(fileType) ? "blob" : "json",
                    withCredentials: true
                }
            )
            .then((res) => {
                const link = document.createElement("a");
                const blob = res.data;

                if (blobFileTypes.has(fileType)) {
                    // For excel and pdf files
                    link.href = window.URL.createObjectURL(blob);
                } else {
                    // For json files
                    link.href = `data:text/json;charset=utf-8,${encodeURIComponent(
                        JSON.stringify(blob)
                    )}`;
                }
                link.download = fileName + exportFileExtentionForAnalyzer[fileType];

                link.click();
                link.remove();
            });
    } catch (error) {
        showError(error);
    }
}

export function* exportMultipleZonesSaga() {
    yield takeLatest(EXPORT_MULTIPLE_ZONES, exportMultipleZones);
}

function* getDataForFilters(
    action: GetDataForAnalyzerFiltersAction
): Generator<
    | Promise<DataForAnalyzerFiltersReceivedAction["payload"]>
    | GeneratorYieldType<DataForAnalyzerFiltersReceivedAction["payload"]>,
    void,
    AnalyzerFilters
> {
    try {
        const json = yield axios
            .get("/api/analysis-zone/analysiszone_listview_data/", {
                params: action.payload.params
            })
            .then((response) => response.data);

        yield put(dataForAnalyzerFiltersReceived({ data: json }));
    } catch (error) {
        showError(error);
    }
}

export function* getDataForFiltersSaga() {
    yield takeLatest(GET_DATA_FOR_ANALYZER_FILTERS, getDataForFilters);
}
